/**
 * Extra Design-step fields for pre-contract intake: roof slope squares and battery selection.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    DEFAULT_BATTERY_MANUFACTURER,
    DEFAULT_BATTERY_MODEL_NAME,
    DEFAULT_BATTERY_QUANTITY,
    parseBuildingRoofSquares,
} from '../../helpers/productDesignFallbacks';
import designServices from '../../services/oneButtonServices/designServices';
import type { PreContractIntakeForm } from '../lib/preContractTypes';

async function fetchBuildingRoofData(salesForceId: string): Promise<unknown | null> {
    const tryParse = (data: unknown) => {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }
        return data;
    };

    try {
        const oneDraw = tryParse(await designServices.RetrieveBuildingWithRoof(salesForceId));
        if (oneDraw && Array.isArray((oneDraw as any).Building) && (oneDraw as any).Building.length) {
            return oneDraw;
        }
    } catch {
        /* try fallback */
    }

    try {
        return tryParse(await designServices.GetRoofAreasByOpportunityId(salesForceId));
    } catch {
        return null;
    }
}

type PreContractRoofSlopeSectionProps = {
    salesForceId: string;
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    installDisplay?: string;
};

/** Roof (Slope) Squares for Solar-only design steps in the intake drawer. */
export const PreContractRoofSlopeSection: React.FC<PreContractRoofSlopeSectionProps> = ({
    salesForceId,
    form,
    patchForm,
}) => {
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const viewerTotalsRef = useRef(form.roofSlopeLowTotal + form.roofSlopeNormalTotal);
    viewerTotalsRef.current = form.roofSlopeLowTotal + form.roofSlopeNormalTotal;

    useEffect(() => {
        let cancelled = false;
        if (!salesForceId.trim()) return undefined;

        setLoading(true);
        setLoadError(null);
        void (async () => {
            const data = await fetchBuildingRoofData(salesForceId.trim());
            if (cancelled) return;
            // Viewer Roof Square Table is the source of truth when it has already posted.
            if (viewerTotalsRef.current > 0) {
                setLoading(false);
                return;
            }
            const parsed = parseBuildingRoofSquares(data);
            if (!parsed) {
                setLoadError('Slope data unavailable — enter overrides to proceed.');
                setLoading(false);
                return;
            }
            patchForm({
                roofSlopeLowTotal: parsed.low,
                roofSlopeNormalTotal: parsed.standard,
            });
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
        // Only re-fetch when opportunity changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesForceId]);

    const overrideTotal =
        Math.trunc(form.roofSlopeLowOverride ?? 0) + Math.trunc(form.roofSlopeNormalOverride ?? 0);
    const calculatedTotal = form.roofSlopeLowTotal + form.roofSlopeNormalTotal;
    const squaresReady =
        (Number(form.roofSlopeLowOverride ?? form.roofSlopeLowTotal) || 0) +
            (Number(form.roofSlopeNormalOverride ?? form.roofSlopeNormalTotal) || 0) >
        0;
    const needsOverride = calculatedTotal <= 0;

    return (
        <section className="pci-design-block pci-design-block--slope">
            <div className="pci-roof-slope">
            <p className="pci-section-label">Roof (Slope) Squares</p>
            {loading && !squaresReady ? (
                <p className="pci-roof-slope__hint">Loading roof squares…</p>
            ) : null}
            {loadError && !squaresReady ? (
                <p className="pci-roof-slope__hint pci-roof-slope__hint--warn">{loadError}</p>
            ) : null}
            {!loading && !squaresReady ? (
                <p className="pci-roof-slope__hint pci-roof-slope__hint--warn">
                    {needsOverride
                        ? 'Totals are 0 — enter override squares to continue.'
                        : 'Roof squares cannot be 0.'}
                </p>
            ) : null}
            <table className="pci-roof-slope__table">
                <thead>
                    <tr>
                        <th>Slope</th>
                        <th>Total</th>
                        <th>Override</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Low</td>
                        <td className="pci-roof-slope__dim">{form.roofSlopeLowTotal}</td>
                        <td>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                className="pci-roof-slope__input"
                                value={form.roofSlopeLowOverride ?? ''}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    patchForm({
                                        roofSlopeLowOverride:
                                            raw === '' ? null : Math.max(0, Math.trunc(Number(raw) || 0)),
                                    });
                                }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Normal</td>
                        <td className="pci-roof-slope__dim">{form.roofSlopeNormalTotal}</td>
                        <td>
                            <input
                                type="number"
                                min={0}
                                step={1}
                                className="pci-roof-slope__input"
                                value={form.roofSlopeNormalOverride ?? ''}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    patchForm({
                                        roofSlopeNormalOverride:
                                            raw === '' ? null : Math.max(0, Math.trunc(Number(raw) || 0)),
                                    });
                                }}
                            />
                        </td>
                    </tr>
                    <tr className="pci-roof-slope__total-row">
                        <td>Total</td>
                        <td className="pci-roof-slope__dim">{calculatedTotal}</td>
                        <td className="pci-roof-slope__override-total">{overrideTotal}</td>
                    </tr>
                </tbody>
            </table>
            </div>
        </section>
    );
};

const BATTERY_MFG_OPTIONS = ['Tesla', 'Enphase', 'Sonnen'] as const;
const BATTERY_NAME_BY_MFG: Record<string, string[]> = {
    Tesla: [DEFAULT_BATTERY_MODEL_NAME, 'Tesla Powerwall 3', 'Tesla Powerwall+'],
    Enphase: ['Enphase IQ Battery 5P', 'Enphase IQ Battery 10C'],
    Sonnen: ['sonnenCore', 'sonnenBatterie 10'],
};
const BATTERY_QTY_OPTIONS = [1, 2, 3, 4];

function defaultBatteryNameForManufacturer(mfg: string): string {
    if (mfg === DEFAULT_BATTERY_MANUFACTURER) return DEFAULT_BATTERY_MODEL_NAME;
    return BATTERY_NAME_BY_MFG[mfg]?.[0] ?? '';
}

type PreContractBatterySectionProps = {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
};

/** Default battery selection (Manufacturer / Name / Quantity) — quantity is user-editable. */
export const PreContractBatterySection: React.FC<PreContractBatterySectionProps> = ({
    form,
    patchForm,
}) => {
    const isTesla = form.batteryManufacturer.trim().toLowerCase() === 'tesla';

    useEffect(() => {
        if (
            form.batteryManufacturer.trim() &&
            form.batteryName.trim() &&
            form.batteryQuantity > 0
        ) {
            return;
        }
        patchForm({
            batteryManufacturer: DEFAULT_BATTERY_MANUFACTURER,
            batteryName: DEFAULT_BATTERY_MODEL_NAME,
            batteryQuantity: DEFAULT_BATTERY_QUANTITY,
        });
        // Apply Tesla defaults once when the battery block first mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nameOptions = useMemo(() => {
        const list = BATTERY_NAME_BY_MFG[form.batteryManufacturer] ?? [];
        if (form.batteryName && !list.includes(form.batteryName)) {
            return [form.batteryName, ...list];
        }
        return list;
    }, [form.batteryManufacturer, form.batteryName]);

    return (
        <div className="pci-battery-select pci-battery-select--inline">
            <p className="pci-section-label">Battery selection</p>
            <div className="pci-battery-select__row">
                <label className="pci-field pci-battery-field">
                    <span className="pci-battery-field__label">
                        Manufacturer <em>*</em>
                    </span>
                    <select
                        value={form.batteryManufacturer || DEFAULT_BATTERY_MANUFACTURER}
                        onChange={(e) => {
                            const mfg = e.target.value;
                            patchForm({
                                batteryManufacturer: mfg,
                                batteryName: defaultBatteryNameForManufacturer(mfg),
                                batteryQuantity:
                                    form.batteryQuantity > 0
                                        ? form.batteryQuantity
                                        : DEFAULT_BATTERY_QUANTITY,
                            });
                        }}
                    >
                        {BATTERY_MFG_OPTIONS.map((m) => (
                            <option key={m} value={m}>
                                {m}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="pci-field pci-battery-field">
                    <span className="pci-battery-field__label">
                        Name <em>*</em>
                    </span>
                    <select
                        value={form.batteryName || defaultBatteryNameForManufacturer(form.batteryManufacturer)}
                        onChange={(e) => patchForm({ batteryName: e.target.value })}
                        disabled={isTesla}
                    >
                        {nameOptions.map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="pci-field pci-battery-field">
                    <span className="pci-battery-field__label">
                        Quantity <em>*</em>
                    </span>
                    <select
                        value={form.batteryQuantity > 0 ? form.batteryQuantity : DEFAULT_BATTERY_QUANTITY}
                        onChange={(e) =>
                            patchForm({
                                batteryQuantity: Math.max(1, Number(e.target.value) || DEFAULT_BATTERY_QUANTITY),
                            })
                        }
                    >
                        {BATTERY_QTY_OPTIONS.map((q) => (
                            <option key={q} value={q}>
                                {q}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        </div>
    );
};
